import api from '../../../services/api';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';
import { PanelError } from '../../performance-operations/config';

// PM analitik P5 — E09 "9-Box ve Yetenek Görünümü" veri katmanı (analiz §12). Uçlar:
//   GET v1/talent-analytics/nine-box/summary      M29–M32, 3×3 matris (sayı + yüzde), eksik eksen, dikkat sayıları
//   GET v1/talent-analytics/nine-box/transitions  önceki süreç hücresi → seçili süreç hücresi (n), hareket özeti
//   GET v1/talent-analytics/nine-box/people       hücre / geçiş / eksik eksen / dikkat listesi (sayfalı)
//   GET v1/nine-box/processes                     süreç seçicisi (mevcut uç)
//   POST v1/development-plans/invite              E09 → E12: gelişim planına davet (mevcut uç, TD.Write)
// Ortak süzgeç (dönem + kapsam) URL'de (pmAnalyticsFilters); süreç seçimi ve liste seçimi de URL'de ayrı anahtarlarla.
// api.js GET'te reject ETMEZ — hata sinyali res.data.status.

export const POT_ROWS = ['high', 'mid', 'low'];     // matris satırları yukarıdan aşağı (potansiyel)
export const PERF_COLS = ['low', 'mid', 'high'];    // matris sütunları soldan sağa (performans)
export const BANDS = ['low', 'mid', 'high'];

// NineBoxCellKeys ile aynı eşleme: (potansiyel, performans) → hücre anahtarı.
export const CELL_BY_BANDS = {
  'low|low': 'attention_needed', 'low|mid': 'reliable_performer', 'low|high': 'expert_master',
  'mid|low': 'developing', 'mid|mid': 'core_employee', 'mid|high': 'high_performer',
  'high|low': 'hidden_talent', 'high|mid': 'promising', 'high|high': 'star_hipo',
};
export const cellOf = (pot, perf) => CELL_BY_BANDS[`${pot}|${perf}`];

export const METRIC_CODES = ['M29', 'M30', 'M31', 'M32'];
export const MISSING_REASONS = ['performanceOnly', 'potentialOnly', 'neither', 'bothNotPlaced'];
export const ATTENTION_HIGH_PERF_NO_POTENTIAL = 'highPerfNoPotential';

// URL anahtarları (ortak süzgeç anahtarlarıyla çakışmaz).
export const Q_PROCESS = 'nbp';
export const Q_PREVIOUS = 'nbprev';
export const Q_SELECTION = 'nbsel';

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

/** Süreç seçimi: boş = sunucu varsayılanı (dönemin en yeni tamamlanmış süreci / başka dönemden önceki süreç). */
export const processParams = ({ processId, previousProcessId } = {}) => ({
  ...(processId ? { processId } : {}),
  ...(previousProcessId ? { previousProcessId } : {}),
});

export const fetchSummary = (filters, processes) => get('/v1/talent-analytics/nine-box/summary', {
  ...toAnalyticsParams(filters), ...processParams(processes),
});

export const fetchTransitions = (filters, processes) => get('/v1/talent-analytics/nine-box/transitions', {
  ...toAnalyticsParams(filters), ...processParams(processes),
});

/** Liste seçimi → uç parametresi. selection: { type: 'cell'|'transition'|'missing'|'attention', ... }. */
export function selectionParams(selection) {
  if (!selection) return {};
  switch (selection.type) {
    case 'cell': return { cell: selection.cell };
    case 'transition': return { fromCell: selection.from, toCell: selection.to };
    case 'missing': return { missing: selection.reason };
    case 'attention': return { attention: selection.attention };
    default: return {};
  }
}

export const fetchPeople = (filters, processes, selection, pageNumber, pageSize) => get('/v1/talent-analytics/nine-box/people', {
  ...toAnalyticsParams(filters), ...processParams(processes), ...selectionParams(selection), pageNumber, pageSize,
});

export const fetchProcesses = async () => {
  const res = await api.get('/v1/nine-box/processes');
  const data = payload(res);
  return Array.isArray(data) ? data.filter((p) => p.status === 'completed') : [];
};

/** E09 → E12: seçilen çalışanları gelişim planı hazırlamaya davet eder (td.idp_invitation). Hata tostu interceptor'da. */
export async function inviteToDevelopmentPlan(employeeIds) {
  try {
    await api.post('/v1/development-plans/invite', { employeeIds });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- URL'deki seçim (geri dönüşte korunur)
export function parseSelection(raw) {
  if (!raw) return null;
  const [type, a, b] = raw.split(':');
  if (type === 'cell' && a) return { type, cell: a };
  if (type === 'transition' && a && b) return { type, from: a, to: b };
  if (type === 'missing' && MISSING_REASONS.includes(a)) return { type, reason: a };
  if (type === 'attention' && a === ATTENTION_HIGH_PERF_NO_POTENTIAL) return { type, attention: a };
  return null;
}

export function serializeSelection(selection) {
  if (!selection) return null;
  switch (selection.type) {
    case 'cell': return `cell:${selection.cell}`;
    case 'transition': return `transition:${selection.from}:${selection.to}`;
    case 'missing': return `missing:${selection.reason}`;
    case 'attention': return `attention:${selection.attention}`;
    default: return null;
  }
}

// ---------------------------------------------------------------- etiketler
/** Eksen anahtarının etiketi: potansiyel görünürken hücre adı, değilse performans bandı. */
export const axisLabel = (t, key, potentialVisible) => (potentialVisible
  ? t(`performance.ninebox.cell.${key}`, { defaultValue: key })
  : `${t('performance.ninebox.performance')} — ${t(`performance.ninebox.${key}`, { defaultValue: key })}`);

export function selectionLabel(t, selection, potentialVisible) {
  if (!selection) return t('talent.nineBoxTransitions.people.all');
  switch (selection.type) {
    case 'cell': return t('talent.nineBoxTransitions.people.inCell', { cell: axisLabel(t, selection.cell, potentialVisible) });
    case 'transition': return t('talent.nineBoxTransitions.people.inTransition', {
      from: axisLabel(t, selection.from, potentialVisible), to: axisLabel(t, selection.to, potentialVisible),
    });
    case 'missing': return t(`talent.nineBoxTransitions.missing.${selection.reason}`);
    case 'attention': return t('talent.nineBoxTransitions.attention.highPerfNoPotential');
    default: return '';
  }
}

/** Geçiş hücresi sayısı (gizli hücre null). */
export const transitionCount = (cells, from, to) => (cells || []).find((c) => c.from === from && c.to === to) || null;

/** Tablo görünümü: sıfır olmayan ya da gizli geçişler, büyükten küçüğe (gizliler sonda). */
export function transitionRows(cells) {
  return (cells || [])
    .filter((c) => c.suppressed || (c.count || 0) > 0)
    .sort((a, b) => (a.suppressed ? 1 : 0) - (b.suppressed ? 1 : 0) || (b.count || 0) - (a.count || 0));
}
